/**
 * How Kimmo moves: the fixed timestep everything is counted in, the player's
 * own dimensions and speeds, the vauhti meter, the second jump, steering and
 * the look.
 *
 * All of it is hashed into the simulation fingerprint - every value here can
 * change a finishing time. See `src/config/AGENTS.md`.
 */

/** Fixed simulation rate. Gameplay never depends on the render frame rate. */
export const SIMULATION_HZ = 120;

export const FIXED_TIMESTEP = 1 / SIMULATION_HZ;

/** Safety valve for the accumulator, prevents the spiral of death on hitches. */
export const MAX_STEPS_PER_FRAME = 10;

/** A single render frame never feeds more than this much time to the sim. */
export const MAX_FRAME_TIME = 0.25;

export const PLAYER = {
  /** Player is simulated as a vertical cylinder. */
  radius: 0.34,
  height: 1.72,
  eyeHeight: 1.6,

  /** Base ground speed in m/s. The whole level is timed against this. */
  maxSpeed: 5.0,
  /** Quake-style acceleration coefficient (higher = snappier). */
  groundAccel: 12.0,
  airAccel: 3.0,
  /** Ground friction coefficient (higher = stops faster). */
  friction: 5.5,
  /** Below this speed friction uses a constant instead of a proportional drop. */
  stopSpeed: 1.4,

  gravity: 22.0,
  /** v = sqrt(2 * g * h); 5.6 with g=22 gives roughly a 0.71 m hop - just enough to clear a sleeping LAN player. */
  jumpVelocity: 5.6,
  /** Grace period after walking off a ledge during which jump still works. */
  coyoteTime: 0.09,
  /** Jump presses are remembered this long before landing. */
  jumpBufferTime: 0.12,

  /**
   * Requested direction plus the beer lurch are clamped to this length.
   * Values above 1 allow a sip aligned with movement to add wish speed.
   */
  maxWishLength: 1.28,

  /** How much steering authority is left while staggered (0 = none). */
  staggerControl: 0.35,
} as const;

/**
 * ==========================================================================
 *  VAUHTI - the momentum meter
 * ==========================================================================
 *  The one number the HUD puts Kimmo's name on, and the reason the level is
 *  worth learning rather than just surviving.
 *
 *  It is a plain multiplier on `PLAYER.maxSpeed`, so it does not change how the
 *  player accelerates or steers - only how fast the ground speed he is
 *  accelerating *towards* is. Keep running and it climbs; touch anything and it
 *  falls off a cliff.
 *
 *    running clean  ->  +`buildPerSecond` per second, up to `max`
 *    jumping        ->  x`jumpFactor`, plus a straight bite out of the speed
 *    hitting a wall ->  x`hitFactor`, plus a bigger bite
 *    Hannu's swipe  ->  x`swipeFactor` (the knockback itself still helps you)
 *
 *  The ordering of those penalties is the whole design: hopping a sleeping LAN
 *  player has to stay cheaper than ploughing into him, or the slalom stops
 *  being a slalom. Keep `jumpFactor` comfortably above `hitFactor`.
 * ==========================================================================
 */
export const MOMENTUM = {
  /** Every run starts here. 1.0 = plain `PLAYER.maxSpeed`. */
  min: 1.0,
  /** Ceiling. 1.9 x 5.0 m/s = 9.5 m/s, i.e. 34 km/h on the HUD. */
  max: 1.9,
  /** Gain per second of clean running. min -> max takes 12 s of not touching anything. */
  buildPerSecond: 0.075,
  /** Below this ground speed you are not running, so nothing builds. */
  buildMinSpeed: 3.2,
  /** Dawdling bleeds the meter back down at this rate. */
  idleDecayPerSecond: 0.6,

  /**
   * Take-off multiplier. A hop is a real cost, just a much smaller one than a
   * crash - and it is the one cost in the game that can be handed back.
   *
   * A jump with a car's boot lid at either end of it is free. `stepPlayer`
   * holds what the take-off took and returns it in full - this,
   * `jumpSpeedFactor` and the clean streak alike - the moment the landing
   * turns out to be a lid; and a jump that *leaves* a lid is never charged in
   * the first place. Land anywhere else, from anywhere else, and it stands.
   *
   * Both ends rather than just the landing, because half of it was a lie you
   * could feel: the lot handed the meter back for getting onto a car and then
   * took it straight off you again for getting down, so a runner who took the
   * lids arrived at the far end behind one who never left the ground. A car on
   * the fast line has to be a step, not a hurdle with a rebate.
   *
   * The lot is the one stretch of the route where leaving the ground is the
   * fast line rather than a tax on it, and charging for it made the shortcut
   * self-defeating: clearing one tail cost exactly the meter needed to clear
   * the next, so a runner who took the lids arrived at the far end slower than
   * one who went round them all. The lids are a line now, not a stunt.
   *
   * The one thing it costs is a safety argument. `CAR.bodyHeight` used to be
   * able to lean on "a runner who has just landed on a boot has spent a jump's
   * worth of meter getting there, so the real figure is lower still". It is
   * not lower any more: `bootHeight` plus a full-meter apex is now honestly
   * reachable, and it is what keeps the roofs off limits on its own.
   */
  jumpFactor: 0.86,
  /**
   * Horizontal speed kept on take-off, so the km/h readout drops the moment
   * you jump. Given back on a lid landing with the rest of it - a jump that
   * still showed up on the readout would not read as free whatever the meter
   * said.
   */
  jumpSpeedFactor: 0.93,

  /**
   * What a free jump is paid for the time it spent off the ground, and what a
   * landing on a lid is paid on top of that.
   *
   * The meter only builds with your feet down (see `buildPerSecond`), which is
   * the right rule for a hop over something and the wrong one for a hop *onto*
   * something: a vault that gave the whole take-off back still quietly cost
   * the half-second of build the same half-second of running would have
   * earned, and the bar told the runner the car had been free. So a free jump
   * is credited `buildPerSecond` for its airtime on landing - not a gain, just
   * the ground rate it would have had anyway.
   *
   * `vaultBonus` is the one part of this that *is* a gain. Landing on a lid is
   * the hardest thing the lot asks for - it is held shut by where you jump
   * from rather than by the apex - and a shortcut that only breaks even with
   * running round is a shortcut nobody has a reason to learn. It is about
   * two thirds of a second of clean running, which is small enough that the
   * lot cannot be farmed for meter and large enough to read on the bar.
   */
  vaultBonus: 0.05,

  /**
   * How much of the meter goes into the legs: extra take-off velocity per unit
   * of *earned* vauhti, as a fraction of `PLAYER.jumpVelocity`.
   *
   * A full meter is worth 14 % on the take-off and 31 % on the apex - 0.713 m
   * cold, 0.933 m flat out. That range is the whole reason the badly parked
   * cars in the lot have a boot lid: `CAR.bootHeight` sits inside it, so the
   * tail you have to steer round at a walk is one you can vault at speed, and
   * `CAR.bodyHeight` sits outside it, so a car is still a car.
   *
   * It was 0.1, and that made the range 134 mm wide from empty bar to full.
   * A hurdle inside a range that narrow is one you clear exactly at your apex,
   * which is a window of zero width - `Level.test.ts` measured the real price
   * of the lid at 93 % of a meter that no line on the route ever fills, i.e.
   * at never. Widening the range is what turns the lid back into a jump with a
   * take-off point rather than a number that only reads as reachable.
   *
   * It has been widened twice since, from 0.1 to 0.14 and then to here, both
   * times because the lid was reading as too hard to be worth aiming at.
   * Measured, this last step takes the price of the first lid from a third of
   * the bar to 0.31 of it and adds a take-off point in the middle of the
   * range. That is a smaller step than it sounds, and the reason is worth
   * knowing before anybody reaches for this lever again: at the meters the lot
   * actually produces, the window is not held shut by the apex at all. It is
   * held shut by *where you jump from*. Nothing lands from closer than 1.7 m
   * to the edge at any value of this, and at the 1.66 the lot gives you six of
   * the nine sweep points already work. This lever buys the runner who arrives
   * slow; it does not buy the runner who arrives badly.
   *
   * The boot plus the full-meter apex must stay below the car roof, or the
   * parking boundary becomes a shortcut. The house precision stacks also
   * stay above a floor jump. Level.test.ts checks both pieces of geometry.
   *
   * It rides on `momentum` alone and deliberately not on `beer.boost`. The
   * bottle is the only way past `MOMENTUM.max` and it is meant to buy speed;
   * letting it buy lift as well would put the apex over a car roof, and the
   * ansalanka's "no third way" would quietly become a fourth.
   */
  jumpBoost: 0.16,

  /** Wall contacts below this normal speed are a brush, not a crash - they are free. */
  hitImpactSpeed: 1.0,
  hitFactor: 0.55,
  /** Horizontal speed kept after a crash, on top of what collision already took. */
  hitSpeedFactor: 0.72,

  /**
   * Hannu. Deliberately gentler than a wall: the swipe already shoves you, and
   * baiting it from behind on the run line is supposed to stay a live option.
   */
  swipeFactor: 0.7,
} as const;

/**
 * ==========================================================================
 *  TUPLAHYPPY - the second press, and the one thing it may not buy
 * ==========================================================================
 *  Press space, let go, and press it again on the way down: the legs come back
 *  under you once, mid-air. It is the only input in the game with a timing
 *  window on it, and the window is the whole mechanic.
 *
 *  ------------------------------------------------------------------------
 *   IT BUYS REACH, AND IT MAY NEVER BUY HEIGHT
 *  ------------------------------------------------------------------------
 *  This is the hard constraint, and it is not a preference. Eight tests in
 *  `Level.test.ts` price the level against `jumpApex(MOMENTUM.max)` as "the
 *  highest anybody can get" - the teltta, the veräjä, the matkailuauto, the
 *  pöytäryhmä and, above all, `CAR.bodyHeight`. A car roof is reachable at
 *  1.72 m and the whole left-hand bay row becomes a road out into the
 *  neighbour's field, which is the sixty-metre skip `AITAUS` was built to
 *  close. The margin under it is 57 mm, measured from a boot lid at a full
 *  meter, and the comment on `CAR` says in as many words that 57 mm is the
 *  whole of it.
 *
 *  So the second jump is arranged so that it *cannot* out-peak the first, at
 *  any meter, by construction rather than by tuning:
 *
 *      it fires no earlier than `windowOpen` x the first jump's rise time,
 *      and it restores `velocityFactor` x the first jump's take-off speed.
 *
 *  Both are multiples of the same arc, so the arithmetic is scale-free. By
 *  the time the window opens you have fallen `(windowOpen - 1)^2` of your
 *  apex; the hop you get back is worth `velocityFactor^2` of it. Keep
 *
 *      velocityFactor < windowOpen - 1
 *
 *  and the peak of a double jump is strictly below the peak of the single
 *  jump it started as - whatever the meter, whatever you are standing on.
 *  `PlayerMovement.test.ts` asserts the inequality on the constants *and*
 *  sweeps the whole meter range against the real physics, because a rule that
 *  is only checked as arithmetic is a rule that survives its own code being
 *  rewritten.
 *
 *  What it does buy is time in the air at a useful height, and that is the
 *  thing the lot has actually always been short of. `MOMENTUM.jumpBoost` says
 *  it outright: at the meters the parkkipaikka produces, the boot lid is not
 *  held shut by the apex, it is held shut by *where you jump from* - nothing
 *  lands from closer than 1.7 m to the edge. A runner who took off early used
 *  to have no answer at all. Now he has one, it costs him, and it still will
 *  not put him on a roof.
 *
 *  ------------------------------------------------------------------------
 *   WHY IT IS EDGE-TRIGGERED, AND WHY THAT IS THE DIFFICULTY
 *  ------------------------------------------------------------------------
 *  The ground jump reads the space bar as *held*, and that has not changed -
 *  nothing about the pirtti, the käytävä or the reference route moves. The
 *  second jump reads a fresh *press*: space has to come up and go down again.
 *  Holding it through the arc does nothing at all, which is what stops the
 *  double jump from being something you get by accident on every hop, and it
 *  is why the reference bot - which holds space for twelve steps and never
 *  releases it - runs exactly the line it always did.
 *
 *  The window is deliberately narrow and deliberately late. On flat ground at
 *  a full meter it opens 0.42 s after take-off and you are back on the floor
 *  at 0.58 s, so what you are actually aiming at is about a sixth of a second
 *  - a real link, not a mash. Pressing early costs nothing and is not
 *  consumed; you may release and try again inside the same jump.
 *
 *  Hashed, all of it: every number here decides where a runner ends up. See
 *  `src/config/CLAUDE.md`.
 * ==========================================================================
 */
export const DOUBLE_JUMP = {
  /**
   * When the window opens, as a multiple of the first jump's *rise time*
   * (`v0 / gravity`) rather than as a wall-clock delay.
   *
   * A multiple rather than a number of seconds because the arc is not one
   * shape: a full meter is in the air half again as long as a cold jump, and
   * a fixed delay that lands after the apex at one end of the meter lands
   * before it at the other. Above 1 means the window cannot open until you
   * are already falling, which is half of the height guarantee above.
   */
  windowOpen: 1.45,
  /** And when it shuts, on the same clock. */
  windowClose: 2.3,
  /**
   * How much of the first jump's take-off speed the second press puts back.
   *
   * Measured against the speed that jump actually left the ground with, held
   * on the player state, and not against what the meter says now - the first
   * jump has already taken its own bite out of that, and a second jump that
   * quietly shrank because of the first one's penalty would make the window
   * pay differently depending on what you did before it.
   *
   * Must stay under `windowOpen - 1`. That is the height guarantee, and it is
   * the one number in this block that is not a matter of taste.
   */
  velocityFactor: 0.42,
  /**
   * What the second press costs the meter, on top of what the take-off
   * already took.
   *
   * Harsher than `MOMENTUM.jumpFactor`, because it has to be: a jump and its
   * double together come to 0.67 of the meter against a single hop's 0.86.
   * That still leaves a jump cheaper than a crash (`MOMENTUM.hitFactor`,
   * 0.55), which is the ordering the whole slalom rests on.
   */
  momentumFactor: 0.78,
  /** And the straight bite out of horizontal speed, read the same way. */
  speedFactor: 0.88,
} as const;

/**
 * Movement inertia (system #2).
 *
 * The desired direction is fed through a spring-damper instead of being applied
 * instantly. `damping` below 1.0 is underdamped => hard left then hard right
 * overshoots the target direction. Keep `frequency` high enough that it reads as
 * momentum rather than input lag.
 */
export const STEERING = {
  /** Natural frequency in rad/s. Higher = more responsive. */
  frequency: 9.5,
  /** Damping ratio. 1.0 = no overshoot, 0.45 ~ 20% overshoot. */
  damping: 0.45,
} as const;

export const LOOK = {
  /** Radians per pixel of mouse movement at sensitivity 1.0. */
  baseSensitivity: 0.0022,
  minPitch: -Math.PI / 2 + 0.02,
  maxPitch: Math.PI / 2 - 0.02,
} as const;
